import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Stack,
  alpha,
  useTheme,
  LinearProgress,
  Collapse,
  ButtonBase,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import TimelineIcon from "@mui/icons-material/Timeline";
import InsightsIcon from "@mui/icons-material/Insights";
import { useDataFetch } from "../../../core/hooks/useDataFetch";
import { useResponsive } from "../../../core/hooks/useResponsive";
import { Config } from "../../../config/config";
import { CurrentQuote } from "./CurrentQuote";
import { AlgoTrendPanel, AlgoScoreBadge } from "./AlgoTrendPanel";
import { ProfitLossHistory } from "./ProfitLossHistory";

const fmtPrice = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtInr = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });
};

function SectionCard({ title, subtitle, icon: Icon, children, minHeight }) {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  return (
    <Box
      sx={{
        borderRadius: 2,
        p: { xs: 1.25, sm: 1.5 },
        height: "100%",
        minHeight: minHeight ?? "auto",
        border: `1px solid ${alpha(primary, 0.14)}`,
        background:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.background.paper, 0.85)
            : `linear-gradient(180deg, #fff 0%, ${alpha(primary, 0.03)} 100%)`,
        boxShadow: `0 8px 24px ${alpha("#000", 0.05)}`,
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1.25 }}>
        {Icon && (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(primary, 0.1),
              color: primary,
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 20 }} />
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={800} letterSpacing="-0.01em">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>
      {children}
    </Box>
  );
}

/**
 * SectionCard that folds away, with a summary that stays readable while collapsed - which is
 * why the trend score sits in the header rather than in the body.
 */
function CollapsibleSectionCard({ title, icon: Icon, summary, defaultExpanded = true, children }) {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: `1px solid ${alpha(primary, 0.14)}`,
        background:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.background.paper, 0.85)
            : `linear-gradient(180deg, #fff 0%, ${alpha(primary, 0.03)} 100%)`,
        boxShadow: `0 8px 24px ${alpha("#000", 0.05)}`,
        overflow: "hidden",
      }}
    >
      <ButtonBase
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          textAlign: "left",
          px: { xs: 1.25, sm: 1.5 },
          py: { xs: 1, sm: 1.25 },
          "&:hover": { bgcolor: alpha(primary, 0.04) },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%", minWidth: 0 }}>
          {Icon && (
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.25,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(primary, 0.1),
                color: primary,
                flexShrink: 0,
              }}
            >
              <Icon sx={{ fontSize: 20 }} />
            </Box>
          )}
          <Typography
            variant="subtitle2"
            fontWeight={800}
            letterSpacing="-0.01em"
            noWrap
            sx={{ minWidth: 0, flexShrink: 1 }}
          >
            {title}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {summary}
          <ExpandMoreIcon
            sx={{
              color: "text.secondary",
              flexShrink: 0,
              transition: "transform 0.2s",
              transform: expanded ? "rotate(180deg)" : "none",
            }}
          />
        </Stack>
      </ButtonBase>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: { xs: 1.25, sm: 1.5 }, pb: { xs: 1.25, sm: 1.5 }, pt: 0.25 }}>{children}</Box>
      </Collapse>
    </Box>
  );
}

const BuyPerformanceDialog = ({ open, onClose, rowData }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const { isPhone, isCompact } = useResponsive();
  const { callAPI } = useDataFetch();
  const [buyPerformanceData, setBuyPerformanceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadBuyPerformanceData = async () => {
    if (!rowData) return;

    setLoading(true);
    setError(null);

    const payload = {
      symbol: rowData.symbol,
      token: rowData.token,
      buyDate: rowData.buyDate || `${new Date().toISOString().split("T")[0]} 00:00:00`,
      qty: parseInt(rowData.qty, 10),
      buyPrice: parseFloat(rowData.buyPrice),
    };

    try {
      const response = await callAPI(Config.ITradeAPI.buyPerformance, payload);
      if (response.status && response.data) {
        setBuyPerformanceData(response.data);
      } else {
        setError(response.error || "Failed to load buy performance data");
      }
    } catch (err) {
      setError("Network error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && rowData) {
      loadBuyPerformanceData();
    } else {
      setBuyPerformanceData(null);
      setError(null);
    }
  }, [open, rowData]);

  /**
   * Header facts. buyDate is deliberately absent: a live holding comes from the broker
   * without one, so it would read blank half the time. The fetched quote wins over the grid
   * row once it arrives, so the figures match the panels below.
   */
  const buyPriceNum = Number(rowData?.buyPrice);
  const currentPriceNum = Number(buyPerformanceData?.currentQuote?.ltp ?? rowData?.ltp);
  const plNum = Number(buyPerformanceData?.profitLoss?.currentPL ?? rowData?.profitLoss);
  const changePct =
    Number.isFinite(buyPriceNum) && buyPriceNum > 0 && Number.isFinite(currentPriceNum)
      ? ((currentPriceNum - buyPriceNum) / buyPriceNum) * 100
      : null;
  const toneFor = (v) =>
    !Number.isFinite(v) ? "text.primary" : v > 0 ? "success.main" : v < 0 ? "error.main" : "text.primary";

  const headerFacts = [];
  if (rowData?.qty != null) headerFacts.push({ label: "Qty", value: String(rowData.qty) });
  if (Number.isFinite(buyPriceNum)) headerFacts.push({ label: "Buy", value: fmtPrice(buyPriceNum) });
  if (Number.isFinite(currentPriceNum)) {
    headerFacts.push({
      label: "Now",
      value: fmtPrice(currentPriceNum),
      extra: changePct == null ? null : `${changePct >= 0 ? "+" : ""}${changePct.toFixed(2)}%`,
      extraTone: toneFor(changePct),
    });
  }
  if (Number.isFinite(plNum)) {
    headerFacts.push({ label: "P&L", value: fmtInr(plNum), tone: toneFor(plNum) });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      // Below 480px the dialog's own margins and rounded corners waste ~15% of an
      // already narrow viewport, so it takes the whole screen instead.
      fullScreen={isPhone}
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: isPhone ? 0 : 3,
          minHeight: isPhone ? "100%" : { sm: 640 },
          maxHeight: isPhone ? "100%" : "92vh",
          border: `1px solid ${alpha(primary, 0.12)}`,
          background:
            theme.palette.mode === "dark"
              ? theme.palette.background.paper
              : `linear-gradient(165deg, ${alpha(primary, 0.06)} 0%, #fff 38%, #fff 100%)`,
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          pr: 1,
          pb: 1.5,
          pt: { xs: 1.5, sm: 2 },
          px: { xs: 1.5, sm: 3 },
          bgcolor: theme.palette.background.paper,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          color: "text.primary",
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 1.5 }} sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: { xs: 38, sm: 48 },
                height: { xs: 38, sm: 48 },
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(primary, 0.12),
                color: primary,
                flexShrink: 0,
                border: `1px solid ${alpha(primary, 0.2)}`,
              }}
            >
              <ShowChartIcon fontSize={isPhone ? "small" : "medium"} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              {/* On phones the symbol alone is the title; "Buy performance" becomes an
                  eyebrow, so a long symbol doesn't get ellipsised away by the prefix. */}
              {isPhone && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={800}
                  sx={{ letterSpacing: "0.07em", textTransform: "uppercase", display: "block", lineHeight: 1.2 }}
                >
                  Buy performance
                </Typography>
              )}
              <Typography
                variant={isPhone ? "subtitle1" : "h6"}
                component="div"
                fontWeight={800}
                letterSpacing="-0.02em"
                noWrap
                title={rowData?.symbol}
                sx={{ color: "text.primary" }}
              >
                {isPhone ? rowData?.symbol || "—" : `Buy performance · ${rowData?.symbol || "—"}`}
              </Typography>
              {headerFacts.length ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  flexWrap="wrap"
                  columnGap={{ xs: 0.75, sm: 1.25 }}
                  rowGap={0.25}
                  sx={{ mt: isPhone ? 0.25 : 0.5 }}
                >
                  {headerFacts.map((fact) => (
                    <Typography
                      key={fact.label}
                      variant={isPhone ? "caption" : "body2"}
                      component="span"
                      color="text.secondary"
                      sx={{ whiteSpace: "nowrap", lineHeight: 1.3 }}
                    >
                      {fact.label}{" "}
                      <Box component="span" sx={{ fontWeight: 800, color: fact.tone || "text.primary" }}>
                        {fact.value}
                      </Box>
                      {fact.extra && (
                        <Box component="span" sx={{ fontWeight: 700, color: fact.extraTone, ml: 0.4 }}>
                          ({fact.extra})
                        </Box>
                      )}
                    </Typography>
                  ))}
                </Stack>
              ) : (
                <Typography
                  variant={isPhone ? "caption" : "body2"}
                  color="text.secondary"
                  sx={{ mt: isPhone ? 0 : 0.35, display: "block" }}
                >
                  Holdings insight
                </Typography>
              )}
            </Box>
          </Stack>
          <IconButton
            aria-label="close"
            onClick={onClose}
            size="small"
            sx={{
              color: "text.secondary",
              bgcolor: alpha(theme.palette.action.hover, 0.5),
              "&:hover": { bgcolor: alpha(theme.palette.action.hover, 0.9) },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ px: { xs: 1.25, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
        {loading ? (
          <Box sx={{ py: 2 }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 3 }} />
            <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 280 }}>
              <CircularProgress size={40} thickness={4} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading performance…
              </Typography>
            </Stack>
          </Box>
        ) : error ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 280,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.error.main, 0.06),
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              px: 2,
            }}
          >
            <Typography variant="body1" color="error" fontWeight={600} textAlign="center">
              {error}
            </Typography>
          </Box>
        ) : buyPerformanceData ? (
          <Stack spacing={{ xs: 1.5, sm: 2.5 }}>
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <SectionCard title="Live quote" subtitle="OHLC, volume & depth" icon={InsightsIcon}>
                  <CurrentQuote data={buyPerformanceData.currentQuote} />
                </SectionCard>
              </Grid>
              <Grid size={{ xs: 12, md: 8 }}>
                <SectionCard
                  title={`Last ${buyPerformanceData.profitLoss?.months || 3} months chart`}
                  subtitle="Daily OHLC candles against your buy price"
                  icon={ShowChartIcon}
                  minHeight={isCompact ? "auto" : 520}
                >
                  <ProfitLossHistory
                    history={buyPerformanceData.profitLoss?.history}
                    months={buyPerformanceData.profitLoss?.months}
                    buyPrice={rowData?.buyPrice}
                  />
                </SectionCard>
              </Grid>
            </Grid>
            {/* One collapsible section per registered algorithm. The API decides what each
                panel shows, so onboarding an algorithm adds a section with no change here.
                The current price is in the dialog header, so it is not repeated per section. */}
            {(buyPerformanceData.trends || []).map((panel) => (
              <CollapsibleSectionCard
                key={panel.algoKey}
                title={panel.algoName}
                icon={TimelineIcon}
                summary={panel.available ? <AlgoScoreBadge score={panel.score} compact={isPhone} /> : null}
              >
                <AlgoTrendPanel panel={panel} price={buyPerformanceData.currentQuote?.ltp} />
              </CollapsibleSectionCard>
            ))}
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 1.25, sm: 2 } }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          fullWidth={isPhone}
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BuyPerformanceDialog;
